import React, { useState } from 'react';
import { Download, Upload, X, FileCode } from 'lucide-react';

export interface SiteExportImportModalProps {
  isOpen: boolean;
  siteId: string;
  onClose: () => void;
  onExport: () => void;
  onImport: (bundleJSON: string) => Promise<void>;
}

export function SiteExportImportModal({
  isOpen,
  siteId,
  onClose,
  onExport,
  onImport,
}: SiteExportImportModalProps): React.JSX.Element | null {
  const [importing, setImporting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setErrorMsg(null);
    try {
      const text = await file.text();
      await onImport(text);
      onClose();
    } catch {
      setErrorMsg('Failed to import bundle. Ensure valid JSON schema.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-lg p-6 bg-card border border-border rounded-xl shadow-xl space-y-6">
        <div className="flex items-center justify-between border-b border-border pb-4">
          <div className="flex items-center gap-2">
            <FileCode className="h-5 w-5 text-primary" />
            <h3 className="font-semibold text-lg text-foreground">Multi-Campus Site Package</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-muted-foreground hover:text-foreground min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {errorMsg && (
          <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-lg">
            {errorMsg}
          </div>
        )}

        <div className="space-y-4">
          <div className="p-4 border border-border rounded-lg bg-background flex flex-col gap-2">
            <span className="font-semibold text-sm text-foreground">Export Full Site Bundle</span>
            <p className="text-xs text-muted-foreground">
              Download theme tokens, global symbols, pages, and forms as a deterministic JSON file for backup or branch replication.
            </p>
            <button
              type="button"
              onClick={onExport}
              className="mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-semibold rounded-lg text-sm hover:opacity-90 min-h-[44px]"
            >
              <Download className="h-4 w-4" />
              <span>Download Site Bundle (JSON)</span>
            </button>
          </div>

          <div className="p-4 border border-border rounded-lg bg-background flex flex-col gap-2">
            <span className="font-semibold text-sm text-foreground">Import & Clone Campus Site</span>
            <p className="text-xs text-muted-foreground">
              Upload a previously exported site bundle. IDs will be remapped cleanly to prevent collisions.
            </p>
            <label className="mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground font-semibold rounded-lg text-sm hover:opacity-90 cursor-pointer min-h-[44px]">
              <Upload className="h-4 w-4" />
              <span>{importing ? 'Importing...' : 'Upload Site Bundle (.json)'}</span>
              <input
                type="file"
                accept=".json,application/json"
                onChange={handleFileUpload}
                disabled={importing}
                className="hidden"
              />
            </label>
          </div>
        </div>

        <div className="text-right pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-medium min-h-[44px]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
